import React from 'react';
import { useScrollReveal } from '../../hooks';
import { CanvasFigure, Draw } from '../../components/figures/CanvasFigure';
import { PillarCard, SectionHeader, ThreeColumnLayout } from '../ThesisSection/layouts';
import type { ThesisPillar } from '../ThesisSection/types';
import { oneDecision, smallBend, versions } from './figures';

// `rest` is the moment each figure shows when motion is reduced: the whole idea, at once.
const pillars: (ThesisPillar & { draw: Draw; rest: number })[] = [
  {
    id: 'sized',
    title: 'Sized to the decision',
    description:
      'A decision model reads the context once and puts a probability on every answer you allow. It writes no text, so it is quick enough for the request path.',
    color: 'pink',
    draw: oneDecision,
    rest: 1.9,
  },
  {
    id: 'trained',
    title: 'Trained on your cases',
    description:
      'General models start the work. Fine-tunes on your own reviewed decisions finish it, for your labels and your edge cases.',
    color: 'mint',
    draw: smallBend,
    rest: 4,
  },
  {
    id: 'improved',
    title: 'Improved by production',
    description:
      'Production keeps teaching it. Each version trains on the decisions your team reviewed since the last one.',
    color: 'amber',
    draw: versions,
    rest: 7,
  },
];

export function OurApproachSection() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section ref={ref} id="approach" className="relative py-24 md:py-36">
      <div className="max-w-wide mx-auto px-6 sm:px-10 md:px-12">
        <SectionHeader
          label="Our approach"
          title={<>A model for each decision, <span className="sm:block">and it never stops learning.</span></>}
          description="A general model can say anything. A decision needs one answer you can act on, in real time."
          isVisible={isVisible}
          labelColor="mint"
        />

        <ThreeColumnLayout>
          {pillars.map((pillar, index) => (
            <PillarCard
              key={pillar.id}
              pillar={pillar}
              index={index}
              animation={<CanvasFigure draw={pillar.draw} rest={pillar.rest} />}
              isVisible={isVisible}
              animationDelay={0.2 + index * 0.1}
            />
          ))}
        </ThreeColumnLayout>
      </div>
    </section>
  );
}

export default OurApproachSection;
